import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Key, ExternalLink, Copy } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useApp } from '../../context/AppContext';
import './Admin.css';

function PainBar({ label, value, max, color }) {
  return (
    <div className="pain-row">
      <div className="pain-label">{label}</div>
      <div className="pain-track">
        <div className="pain-fill" style={{ width: `${(value / max) * 100}%`, background: color }} />
      </div>
      <div className="pain-count">{value}</div>
    </div>
  );
}

function ApiKeyModal({ onClose }) {
  const { apiKey, saveApiKey } = useApp();
  const [val, setVal] = useState(apiKey);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <h3 style={{ color: 'var(--brown)', marginBottom: '0.5rem' }}>مفتاح Gemini API</h3>
        <p style={{ fontSize: '13px', color: 'var(--brown-muted)', marginBottom: '1rem' }}>
          أدخل مفتاح Gemini API من <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" style={{ color: 'var(--brown)', fontWeight: 600 }}>aistudio.google.com</a> — يُحفظ للجميع تلقائياً.
        </p>
        <div className="form-group">
          <label className="form-label">API Key</label>
          <input
            className="form-input"
            type="password"
            value={val}
            onChange={e => setVal(e.target.value)}
            placeholder="AIza..."
            style={{ fontFamily: 'var(--font-mono)', direction: 'ltr', textAlign: 'left' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { saveApiKey(val); onClose(); }}>حفظ للجميع</button>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { stats, sessions, agents, apiKey, deleteAgent } = useApp();
  const [showApiModal, setShowApiModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);

  const completedSessions = sessions.filter(s => s.status === 'completed');
  const recentSessions = sessions.slice(0, 6);

  const allPainPoints = completedSessions.flatMap(s => s.analysis?.painPoints || []);
  const painCounts = allPainPoints.reduce((acc, p) => {
    acc[p.label] = (acc[p.label] || 0) + 1;
    return acc;
  }, {});
  const topPains = Object.entries(painCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxPain = topPains[0]?.[1] || 1;

  const copyLink = (agentId) => {
    const url = `${window.location.origin}/interview/${agentId}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(agentId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const statusLabel = { completed: 'مكتمل', 'in-progress': 'جارٍ', waiting: 'انتظار' };
  const statusClass = { completed: 'status-done', 'in-progress': 'status-progress', waiting: 'status-waiting' };

  return (
    <AdminLayout
      title="لوحة التحكم"
      subtitle={`آخر تحديث: ${new Date().toLocaleTimeString('ar-SA')}`}
      actions={
        <>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowApiModal(true)}>
            <Key size={14} />
            {apiKey ? 'API: مفعّل ✓' : 'إعداد API'}
          </button>
          <Link to="/admin/agents/new" className="btn btn-primary btn-sm">
            <Plus size={14} />
            وكيل جديد
          </Link>
        </>
      }
    >
      {!apiKey && (
        <div className="alert-banner">
          ⚠️ لم يتم إعداد مفتاح API بعد.
          <button className="alert-link" onClick={() => setShowApiModal(true)}>إعداد الآن</button>
        </div>
      )}

      {/* Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">إجمالي المقابلات</div>
          <div className="metric-value">{stats.total}</div>
          <div className="metric-badge green">نشط</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">مكتملة</div>
          <div className="metric-value">{stats.completed}</div>
          <div className="metric-badge green">{stats.total ? Math.round((stats.completed / stats.total) * 100) : 0}% معدل الإكمال</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">وكلاء نشطون</div>
          <div className="metric-value">{stats.activeAgents}</div>
          <div className="metric-badge yellow">متاح</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">نقاط الألم</div>
          <div className="metric-value">{stats.painPoints}</div>
          <div className="metric-badge red">مرصودة</div>
        </div>
      </div>

      <div className="dash-grid">
        {/* Pain points */}
        <div className="card">
          <div className="card-header">
            <h3>نقاط الألم الأكثر تكراراً</h3>
            <Link to="/admin/insights" className="card-link">عرض الكل ←</Link>
          </div>
          {topPains.length === 0 ? (
            <div className="empty-state"><p>لا توجد بيانات بعد</p></div>
          ) : (
            topPains.map(([label, count], i) => (
              <PainBar key={i} label={label} value={count} max={maxPain}
                color={count / maxPain > 0.7 ? 'var(--red)' : count / maxPain > 0.4 ? 'var(--yellow)' : 'var(--green)'} />
            ))
          )}
        </div>

        {/* Recent sessions */}
        <div className="card">
          <div className="card-header">
            <h3>آخر المقابلات</h3>
            <Link to="/admin/results" className="card-link">عرض الكل ←</Link>
          </div>
          <div className="session-list">
            {recentSessions.map(s => (
              <Link key={s.id} to={`/admin/results/${s.id}`} className="session-row">
                <div>
                  <div className="session-id">#{s.id}</div>
                  <div className="session-time">
                    {new Date(s.createdAt).toLocaleString('ar-SA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <span className={`status ${statusClass[s.status]}`}>{statusLabel[s.status]}</span>
              </Link>
            ))}
            {recentSessions.length === 0 && <div className="empty-state"><p>لا توجد مقابلات بعد</p></div>}
          </div>
        </div>

        {/* Agents */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header">
            <h3>الوكلاء النشطون</h3>
            <Link to="/admin/agents/new" className="btn btn-yellow btn-sm">+ وكيل جديد</Link>
          </div>
          <div className="agents-grid">
            {agents.map(agent => (
              <div key={agent.id} className="agent-card">
                <div className="agent-card-name">{agent.name}</div>
                <div className="agent-card-meta">
                  {sessions.filter(s => s.agentId === agent.id).length} مقابلة
                </div>
                <div className="agent-card-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => copyLink(agent.id)}>
                    <Copy size={12} />
                    {copiedLink === agent.id ? 'تم النسخ!' : 'نسخ الرابط'}
                  </button>
                  <a href={`/interview/${agent.id}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                    <ExternalLink size={12} />
                    فتح
                  </a>
                  <button
                    className="btn btn-sm"
                    style={{ background: 'var(--red-bg)', color: 'var(--red-text)' }}
                    onClick={() => { if (window.confirm('هل أنت متأكد من حذف هذا الوكيل وجميع مقابلاته؟')) deleteAgent(agent.id); }}
                  >
                    حذف
                  </button>
                </div>
              </div>
            ))}
            {agents.length === 0 && <div className="empty-state"><p>لا توجد وكلاء بعد. أنشئ وكيلاً أولاً.</p></div>}
          </div>
        </div>
      </div>

      {showApiModal && <ApiKeyModal onClose={() => setShowApiModal(false)} />}
    </AdminLayout>
  );
}
